import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { api } from './http';
import { sessionManager } from './sessionManager';
import { getStoredAccessToken } from './accessTokenStore';

interface QueuedRequest {
  config: AxiosRequestConfig;
  resolve: (value: AxiosResponse) => void;
  reject: (error: unknown) => void;
}

class RequestQueueManager {
  private queue: QueuedRequest[] = [];
  private processing = false;
  private readonly isDebug = !(
    (import.meta as unknown as { env?: { PROD?: boolean } }).env?.PROD
  );

  async handleUnauthorized(error: AxiosError): Promise<AxiosResponse> {
    // If the app is already in reauth mode, do NOT keep queueing requests.
    // Otherwise long-running flows (uploads, polling, react-query) can produce an
    // unbounded queue and effectively freeze the tab.
    if (sessionManager.isReauthRequired()) {
      return Promise.reject(new Error('Reauthentication required'));
    }

    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    const isRetry = (originalRequest as AxiosRequestConfig & { _retry?: boolean })
      ._retry;
    if (isRetry) {
      return Promise.reject(error);
    }

    if (this.isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      this.queue.push({ config: originalRequest, resolve, reject });
      if (this.isDebug) {
        console.info('[RequestQueue] Queued request', {
          url: originalRequest.url,
          queueSize: this.queue.length,
        });
      }
      void this.processQueue();
    });
  }

  private async processQueue() {
    if (this.processing) return;
    this.processing = true;

    try {
      // If we refreshed successfully less than a minute ago, retry immediately
      // without another refresh. Prevents cascade when many parallel requests (e.g.
      // 10 file uploads) all get 401 and each batch would otherwise trigger refresh.
      if (sessionManager.wasRecentlyRefreshed(60 * 1000)) {
        if (this.isDebug) {
          console.info('[RequestQueue] Skipping refresh, recently refreshed – retrying directly');
        }
        await this.flushQueue();
        return;
      }

      await sessionManager.ensureValidSession('401');
      await this.flushQueue();
    } catch (error) {
      if (this.isDebug) {
        console.warn('[RequestQueue] Refresh failed', error);
      }
      if (this.shouldWaitForReauth(error)) {
        try {
          await sessionManager.ensureValidSession('reauth');
          await this.flushQueue();
        } catch (reauthError) {
          sessionManager.triggerReauth('Refresh failed after reauth');
          this.rejectQueue(reauthError);
        }
      } else {
        this.rejectQueue(error);
      }
    } finally {
      this.processing = false;
      if (this.queue.length > 0) {
        void this.processQueue();
      }
    }
  }

  private async flushQueue() {
    const pending = [...this.queue];
    this.queue = [];

    const storedToken = getStoredAccessToken();
    if (this.isDebug && storedToken) {
      const tokenPreview = storedToken.substring(0, 20) + '...' + storedToken.substring(storedToken.length - 8);
      console.info('[RequestQueue] Retrying with stored token:', tokenPreview);
      console.info('[RequestQueue] Cookie value:', document.cookie.split(';').find(c => c.includes('access_token'))?.trim());
    }

    await Promise.all(
      pending.map(async item => {
        const config = item.config as AxiosRequestConfig & { _retry?: boolean };
        config._retry = true;

        try {
          const response = await api(config);
          item.resolve(response);
        } catch (error) {
          if (this.isDebug && error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as AxiosError;
            const failureReason = axiosError.response?.headers?.['x-auth-failure-reason'];
            if (failureReason) {
              console.error('[RequestQueue] Auth failure reason:', failureReason);
            }
          }
          item.reject(error);
        }
      })
    );
  }

  private rejectQueue(error: unknown) {
    this.queue.forEach(item => item.reject(error));
    this.queue = [];
  }

  private shouldWaitForReauth(error: unknown) {
    if (error instanceof Error) {
      return error.message.toLowerCase().includes('reauth');
    }
    return false;
  }

  private isAuthEndpoint(url?: string) {
    return (
      !!url &&
      (url.includes('/auth/login') ||
        url.includes('auth/login') ||
        url.includes('/auth/refresh_token') ||
        url.includes('auth/refresh_token') ||
        url.includes('/v1/users/profile') ||
        url.includes('v1/users/profile'))
    );
  }
}

export const requestQueueManager = new RequestQueueManager();
